<template>
  <main class="contact-parent">
    <!-- Contact form -->
    <article v-if="status !== 'SENT'" class="form-parent">
      <div class="form-margin">
        <AdaptiveSpace :adaptiveHeight="['82px', '142px']" />
        <h1 class="contact-title">{{ $t('contact.title') }}</h1>
        <p class="contact-subtitle">
          {{ $t('contact.subtitle') }}
        </p>
        <form class="form" v-on:submit.prevent>
          <TextInput column="1" :updateState="updateState" :data="name" />
          <TextInput column="2" :updateState="updateState" :data="company" />
          <TextInput column="1" :updateState="updateState" :data="phone" />
          <TextInput column="2" :updateState="updateState" :data="email" />
          <Message :updateState="updateState" :data="message" />
          <p class="terms">
            <span>
              <input
                type="checkbox"
                class="checkbox"
                :checked="conditions"
                @click="updateState({ event: 'checkbox', payload: conditions })"
              />
            </span>
            {{ $t('contact.accept') }}
            <router-link class="terms-link" :to="{ path: 'terms' }">
              {{ $t('contact.terms') }}</router-link
            >
            {{ $t('contact.and') }}
            <router-link class="terms-link" :to="{ path: 'privacy' }">
              {{ $t('contact.privacy_policy') }}</router-link
            >
          </p>
          <button
            :data-state="status"
            :disabled="!conditions"
            class="button-submit"
            @click="handleFormSubmit"
          >
            <div class="button-text">
              {{ $t('contact.button') }}
            </div>
            <span class="dots">
              <span class="dot" style="--i: 0;" />
              <span class="dot" style="--i: 1;" />
              <span class="dot" style="--i: 2;" />
            </span>
          </button>
          <div class="form-error" v-if="status === 'ERROR'">
            {{ $t('contact.unknown_error') }}
          </div>
        </form>
      </div>
      <AdaptiveSpace staticHeight="70px" />
    </article>
    <SuccessScreen v-if="status === 'SENT'" />
    <section class="image-wrapper">
      <picture>
        <source
          type="image/webp"
          :srcset="
            SHOW_NON_QWELLO_POLES
              ? require('@/assets/contact/start-charging-mobile-POLAND.webp')
              : require('@/assets/contact/start-charging-mobile.webp')
          "
        />
        <img
          :src="
            SHOW_NON_QWELLO_POLES
              ? require('@/assets/contact/start-charging-mobile-POLAND.jpg')
              : require('@/assets/contact/start-charging-mobile.png')
          "
          class="pole-image-mobile"
        />
      </picture>
      <picture>
        <source
          type="image/webp"
          :srcset="
            SHOW_NON_QWELLO_POLES
              ? require('@/assets/contact/start-charging-desktop-POLAND.webp')
              : require('@/assets/contact/start-charging-desktop.webp')
          "
        />
        <img
          :src="
            SHOW_NON_QWELLO_POLES
              ? require('@/assets/contact/start-charging-desktop-POLAND.jpg')
              : require('@/assets/contact/start-charging-desktop.png')
          "
          class="pole-image-desktop"
        />
      </picture>
      <div
        class="contact-text-wrapper"
        :hide="entity === 'SPAIN' ? true : false"
      >
        <div class="contact-child business" v-show="shouldShowBusinessNumber">
          <p class="text-title">{{ $t('contact.inquiries_title') }}</p>
          <p>
            <a
              class="phone-number"
              :href="`tel:${$t('contact.inquiries_phone')}`"
              ><span class="svg-wrapper"
                ><img
                  src="~@/assets/contact/contact-phone.svg"
                  class="contact-svg"/></span
              >{{ $t('contact.inquiries_phone') }}
            </a>
          </p>
          <p class="email">
            <a :href="`mailto:${$t('contact.inquiries_mail')}`">
              <span class="svg-wrapper"
                ><img
                  src="~@/assets/contact/contact-mail.svg"
                  class="contact-svg"/></span
              >{{ $t('contact.inquiries_mail') }}
            </a>
          </p>
        </div>
        <div class="spacing" />
        <div
          class="contact-child customer"
          :align="!shouldShowBusinessNumber ? 'center' : ''"
          :style="customerRowPosition()"
        >
          <p class="text-title">
            {{ $t('contact.support_title') }}
          </p>
          <p>
            <a class="phone-number" :href="`tel:${$t('contact.support_phone')}`"
              ><span class="svg-wrapper"
                ><img
                  src="~@/assets/contact/contact-phone.svg"
                  class="contact-svg"/></span
              >{{ $t('contact.support_phone') }}</a
            >
          </p>
          <p class="email">
            <a :href="`mailto:${$t('contact.support_mail')}`">
              <span class="svg-wrapper"
                ><img
                  src="~@/assets/contact/contact-mail.svg"
                  class="contact-svg"/></span
              >{{ $t('contact.support_mail') }}
            </a>
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import SuccessScreen from '../components/ContactPage/SuccessScreen.vue';
import AdaptiveSpace from '../components/Registration/AdaptiveSpace.vue';
import TextInput from '../components/ContactPage/TextInput.vue';
import Message from '../components/ContactPage/Message.vue';
import {
  getRecaptchaToken,
  loadGoogleCaptcha,
  unloadGoogleCaptcha,
} from '../shared/recaptchaHelper';
import {
  clearError,
  activate,
  findActiveInputs,
  isInValid,
  testErrorMessage,
  createFormData,
} from '../components/ContactPage/helpers';
import initialState from '../components/ContactPage/initialState';
import { shouldShowBusinessNumber } from '../shared/entityHelpers';

export default {
  components: { AdaptiveSpace, TextInput, Message, SuccessScreen },
  name: 'ContactForm',
  data() {
    return initialState();
  },
  mounted() {
    loadGoogleCaptcha();
  },
  beforeDestroy() {
    unloadGoogleCaptcha();
  },
  computed: {
    shouldShowBusinessNumber,
  },
  methods: {
    customerRowPosition() {
      if (this.shouldShowBusinessNumber) {
        return {};
      }
      if (this.entity === 'SPAIN') {
        return {
          '--customer-grid-row': 1,
        };
      }
      return {
        '--customer-grid-row': 2,
      };
    },
    handleFormSubmit() {
      const invalidInputs = [
        this.name,
        this.phone,
        this.email,
        this.message,
        ...(this.company.active ? [this.company] : []), // only add company if active
      ].filter(input => isInValid(input.key, input));
      if (invalidInputs.length > 0) {
        // Show error message for all invalid inputs
        invalidInputs.forEach(input => testErrorMessage.call(this, input.key));
        return;
      }

      this.sendToBackend();
    },
    async sendToBackend() {
      const token = await getRecaptchaToken('CONTACT');
      const formData = createFormData({
        name: this.name.text,
        phone: this.phone.text,
        message: this.message.text,
        email: this.email.text,
        company: this.company.text,
        recaptchaToken: token,
      });

      this.status = 'FETCHING'; // show loading icon if fetching

      const res = await fetch(
        `${process.env.VUE_APP_BACKEND_URL}/1.0/helpdesk/web-form-ticket`,
        {
          method: 'post',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
        }
      );

      if (res.status === 200) {
        // go to success screen
        this.status = 'SENT';
      } else {
        this.status = 'ERROR';
      }
    },
    updateState({ event, payload }) {
      // clear error

      if (!['checkbox', 'validate'].includes(event)) {
        clearError.call(this, event);
        activate.call(this, event);
      }
      switch (event) {
        case 'name':
          this.name.text = payload;
          this.name.state = 'active';
          break;
        case 'company':
          this.company.text = payload;
          if (payload.length === 0) {
            this.company.state = 'default';
            this.company.active = false;
          } else {
            this.company.state = 'active';
          }
          break;
        case 'phone':
          this.phone.text = payload;
          this.phone.state = 'active';
          break;
        case 'email':
          this.email.text = payload;
          this.email.state = 'active';
          break;
        case 'message': {
          this.message.state = 'active';
          if (payload.length >= 1000) {
            // show error
            // this.message.text = payload.length;
            this.message.wordCount = 0;
            this.message.state = 'error';
            this.message.text = payload.slice(0, 1000);
            return;
          }
          this.message.wordCount = 1000 - payload.length;
          this.message.text = payload;
          break;
        }
        case 'checkbox':
          this.conditions = !this.conditions;
          break;
        case 'validate':
          {
            const activeInputs = findActiveInputs(
              this.name,
              this.company,
              this.phone,
              this.email
            );
            const invalidInputs = activeInputs.filter(input =>
              isInValid(input.key, input)
            );
            invalidInputs.forEach(invalidInput => {
              testErrorMessage.call(this, invalidInput.key);
            });
          }
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
.contact-parent {
  --template-columns: 1fr;
  --template-rows: 2fr auto;
  --contact-title-f-size: 32px;
  --contact-subtitle-f-size: 14px;
  --contact-terms-f-size: 14px;
  --contact-btn-f-size: 14px;
  --contact-details-title: 16px;
  --contact-details-text: 14px;
  min-height: 100vh;
  display: grid;
  grid-template-columns: var(--template-columns);
  grid-template-rows: var(--template-rows);
}
.form-margin {
  margin: 0 36px;
}
.contact-title {
  line-height: 1;
  font-size: var(--contact-title-f-size);
  color: #2b4047;
  font-weight: bold;
  font-family: Foco;
  margin-bottom: 15px;
}
.contact-subtitle {
  color: #41565d;
  font-size: var(--contact-subtitle-f-size);
  margin-bottom: 35px;
}
.form {
  display: grid;
}
.form-parent {
  height: 100%;
}
.terms {
  color: #2b4047;
  margin-bottom: 1em;
  margin-top: 1em;
  font-size: var(--contact-terms-f-size);
}
.terms-link {
  text-decoration: underline;
}
.button-submit {
  width: 100%;
  background: #ffdf22;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 30px;
  color: #2b4047;
  height: 35px;
  opacity: 1;
  display: grid;
  outline: 0;
  grid-template-columns: 1fr auto 1fr;
  place-items: center;
}
.button-text {
  grid-column: 2;
  font-size: var(--contact-btn-f-size);
}
.dots {
  grid-column: 3;
  display: inline-block;
  justify-self: flex-start;
  margin-left: 1em;
}
.dot {
  display: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  background: #2b4047;
}
.button-submit[data-state='FETCHING'] .dot {
  display: inline-block;
  animation: loading 1s linear infinite;
  animation-delay: calc(var(--i) * 0.2s);
}

@keyframes loading {
  0% {
    transform: translateY(0%);
  }
  25% {
    transform: translateY(-40%);
  }
  50% {
    transform: translateY(-80%);
  }
  75% {
    transform: translateY(10%);
  }
  100% {
    transform: translateY(0%);
  }
}

/* Unknown Error */

.form-error {
  margin-top: 0.6em;
  position: relative;
  border: 1px solid #f15c5e;
  padding: 1em 2em;
  border-radius: 12px;
  color: #f15c5e;
}
.form-error::before {
  content: '';
  position: absolute;
  border-radius: 12px;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  width: 100%;
  height: 100%;
  background: #f15c5e;
  opacity: 0.1;
}

.button-submit:disabled {
  opacity: 0.5;
}
.image-wrapper {
  position: relative;
  grid-column: 1;
  height: 100%;
  max-height: 290px;
}
.pole-image-mobile,
.pole-image-desktop {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover;
}
.pole-image-mobile {
  display: block;
}
.pole-image-desktop {
  display: none;
}
.contact-text-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0px;
  right: 0px;
  top: 0px;
  bottom: 0px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: 1fr auto 1fr;
  color: white;
  font-size: 14px;
}
.contact-text-wrapper[hide='true'] {
  display: grid;
  place-items: center;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}
.spacing {
  height: 35px;
  grid-column: 2;
  grid-row: 2;
}
.contact-child {
  grid-column: 2;
}
.business {
  align-self: flex-end;
}
.contact-text-wrapper[hide='true'] .business {
  display: none;
}
.contact-text-wrapper[hide='true'] .business {
  visibility: hidden;
}
.contact-text-wrapper[hide='true'] .contact-child {
  grid-column: 1;
}
.customer[align='center'] {
  grid-row: var(--customer-grid-row);
}
.contact {
  align-self: flex-start;
}
.contact-text-wrapper div > p:not(:first-child) {
  margin-top: 2px;
}
.text-title {
  color: #7dcc93;
  font-size: var(--contact-details-title);
}
.phone-number {
  font-size: var(--contact-details-text);
}
.email {
  font-size: var(--contact-details-text);
}
.svg-wrapper {
  margin-right: 10px;
}
.contact-svg {
  display: inline-block;
}
@media (min-width: 768px) {
  .form-margin {
    max-width: 500px;
    margin: 0 auto;
  }
}

@media (min-width: 1224px) {
  .contact-text-wrapper {
    grid-template-columns: 0.3fr auto 1fr;
  }
  .pole-image-mobile {
    display: none;
  }
  .pole-image-desktop {
    display: block;
  }
  .form-margin {
    margin: 0 auto;
    max-width: 680px;
  }
  .contact-parent {
    --template-columns: 5.5fr 3fr;
    --template-rows: auto;
  }
  .image-wrapper {
    grid-column: 2;
    max-height: none;
  }

  .form {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    column-gap: 40px;
  }
  .terms,
  .button {
    grid-column: 1 / -1;
  }
}

@media (min-width: 1600px) {
  .contact-parent {
    --template-columns: 4fr 3fr;
    --contact-title-f-size: 64px;
    --contact-subtitle-f-size: 16px;
    --contact-terms-f-size: 16px;
    --contact-btn-f-size: 20px;
    --contact-details-title: 20px;
    --contact-details-text: 16px;
  }
  .image-wrapper {
    max-height: 100vh;
  }
  .form-margin {
    max-width: 720px;
  }
}
</style>
